// src/schemas/shapes/v3/delivery-options.ts
//
// Eightpoint: optional fields the backend adds to secret and receipt
// payloads when a secret uses the delivery options (view limit, recipient
// verification, multiple recipients). Every field is optional: secrets that
// use none of the options get the upstream payload unchanged.
// Backend: apps/api/v2/logic/secrets/delivery_options.rb

import { z } from 'zod';

/** Fields added to a secret record (GET /secret/:id, POST /secret/:id/reveal). */
export const secretDeliveryFields = {
  max_views: z.number().int().optional(),
  views_remaining: z.number().int().optional(),
  verify_recipient: z.boolean().optional(),
  recipient_verified: z.boolean().optional(),
  recipient_hint: z.string().nullish(),
};

export const recipientLinkStatusValues = [
  'pending',
  'opened',
  'revealed',
  'burned',
  'expired',
  'orphaned',
] as const;

/** One recipient's link, as listed on the sender's receipt. */
export const recipientLinkSchema = z.object({
  identifier: z.string(),
  shortid: z.string(),
  recipient: z.string().nullish(),
  status: z.enum(recipientLinkStatusValues).catch('pending'),
  max_views: z.number().int(),
  views_used: z.number().int(),
  views_remaining: z.number().int(),
  verify_recipient: z.boolean().optional(),
  revealed: z.number().nullish(),
  burned: z.number().nullish(),
});

/** Fields added to a receipt record (GET /receipt/:id). */
export const receiptDeliveryFields = {
  max_views: z.number().int().optional(),
  views_used: z.number().int().optional(),
  views_remaining: z.number().int().optional(),
  verify_recipient: z.boolean().optional(),
  bundle: z.array(recipientLinkSchema).optional(),
};

export type RecipientLink = z.infer<typeof recipientLinkSchema>;
export type RecipientLinkStatus = (typeof recipientLinkStatusValues)[number];

export const MAX_RECIPIENTS = 20;
export const MAX_VIEWS_LIMIT = 10;

const RECIPIENT_SEPARATOR = /[,;\s]+/;

/** Split what the sender typed into addresses (trimmed, deduped, lowercased). */
export function parseRecipientList(value: string | string[] | null | undefined): string[] {
  const parts = (Array.isArray(value) ? value : [value ?? ''])
    .flatMap((entry) => String(entry ?? '').split(RECIPIENT_SEPARATOR))
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => entry.length > 0);
  return Array.from(new Set(parts));
}

const emailSchema = z.email();

/**
 * Form field for the recipient input: a string of addresses separated by
 * commas, semicolons or spaces. Empty is fine (no recipient).
 */
export const recipientListField = z
  .string()
  .optional()
  .superRefine((value, ctx) => {
    const problem = recipientListError(value);
    if (!problem) return;
    ctx.addIssue({
      code: 'custom',
      message:
        problem.kind === 'too_many'
          ? `You can send to at most ${MAX_RECIPIENTS} recipients at once.`
          : `Check this email address: ${problem.email}`,
    });
  });

/** First problem with a recipient list, or null when it is fine. */
export function recipientListError(
  value: string | null | undefined
): { kind: 'invalid'; email: string } | { kind: 'too_many' } | null {
  const list = parseRecipientList(value);
  const invalid = list.find((entry) => !emailSchema.safeParse(entry).success);
  if (invalid) return { kind: 'invalid', email: invalid };
  if (list.length > MAX_RECIPIENTS) return { kind: 'too_many' };
  return null;
}
